import { Account } from '@citrite/sf-api';
import { isWatermarkRidsPresent } from '@sharefiledev/fileviewer-pilet-sdk';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import {
	PermissionByType,
	PermissionsConfigurationsByType,
	PresetsByResourceType,
	PresetsByType,
	SelectedResource,
} from '@sharefiledev/sharing-pilet-sdk';
import {
	AvailablePermissionSet,
	ConditionalLinkConditions,
	ConditionalLinkOptions,
	ConditionalLinkShareType,
	ExistingPermission,
	LinkSettings,
	Preset,
	SharablePreset,
	SharableRelatedResource,
	User,
} from '@sharefiledev/sharing-pilet-sdk/api';
import { PermissionsSelectionByType } from '../../../PermissionsContext/PluginContextProvider';
import { PermissionId } from '../../../ram-types';
import { PermissionsOptionsByType } from '../../../Share/PermissionSelect';
import { getFileExtension } from '../../../utils/permission-utils';
import {
	ConditionalLinkOptionsModalContentType,
	DEFAULT_EXPIRATION_DAYS,
	EditableFiles,
	PermissionName,
	ResourceTypeLiteral,
} from '../../constants';
import {
	getRidsWithoutFileVersion,
	getRidWithoutFileVersion,
} from '../../helpers/GetBaseRids';

export function getLinkPermissionTextKey(
	permission: string,
	condition: ConditionalLinkConditions,
	isWaterMarkingEnabled: boolean,
	selectedFiles: SelectedResource[],
	consumerPluginName?: string
): string {
	if (!condition || !permission) {
		return 'linkBasedShareModal.permissions_banner.link.no_message_available';
	}

	const watermarkSuffix =
		isWaterMarkingEnabled &&
		isStoragePlugin(consumerPluginName) &&
		(permission === PermissionName.View || permission === PermissionName.Download)
			? '_watermark'
			: '';
	const permissionKey = permission.toLowerCase().replace(' ', '_');
	const conditionKey = condition.toLowerCase();

	if (permission === PermissionName.Edit && isStoragePlugin(consumerPluginName)) {
		const fallbackPermission = isEditAvailableForResources(selectedFiles)
			? permissionKey
			: PermissionName.Download.toLowerCase();
		return `linkBasedShareModal.permissions_banner.link.${conditionKey}.${fallbackPermission}${watermarkSuffix}`;
	}

	return `linkBasedShareModal.permissions_banner.link.${conditionKey}.${permissionKey}${watermarkSuffix}`;
}

export const defaultConditions: ConditionalLinkConditions[] = [
	ConditionalLinkConditions.Anonymous,
	ConditionalLinkConditions.AnonymousWithUserInfo,
];

export const isFolder = (selectedResource: SelectedResource): boolean => {
	const folderPattern = /\/folder-[^/]+$/;
	return folderPattern.test(selectedResource.rid);
};

export const defaultPermissions: PermissionName[] = [PermissionName.View];

export function isEditAvailableForResources(
	selectedResources: SelectedResource[]
): boolean {
	return selectedResources.every(resource => {
		if (isFolder(resource)) {
			return true;
		}
		const extension = getFileExtension(resource.name);
		return Object.values(EditableFiles).includes(extension as EditableFiles);
	});
}

export const getPermissionsFromEvaluateResponse = (
	sharablePreset?: SharablePreset[],
	selectedResources?: SelectedResource[],
	isEditAstOn?: boolean,
	pluginName?: string
): string[] => {
	if (!sharablePreset) {
		return defaultPermissions;
	}

	let sharableConditionalLinkPermission = sharablePreset
		.filter(preset => preset.actions?.canShare)
		.map(preset => preset.name)
		.filter((presetName): presetName is string => !!presetName);
	if (!isStoragePlugin(pluginName)) {
		return sharableConditionalLinkPermission;
	}

	sharableConditionalLinkPermission = sharableConditionalLinkPermission.filter(
		presetName => presetName !== PermissionName.Owner
	);

	if (!isEditAstOn || !isEditAvailableForResources(selectedResources!)) {
		sharableConditionalLinkPermission = sharableConditionalLinkPermission.filter(
			presetName => presetName !== PermissionName.Edit
		);
	}
	sharableConditionalLinkPermission = sharableConditionalLinkPermission.filter(
		presetName =>
			presetName === PermissionName.View ||
			presetName === PermissionName.Download ||
			presetName === PermissionName.Edit
	);
	return sharableConditionalLinkPermission;
};

export const getPermissionsByTypeFromEvaluateResponse = (
	sharablePresetByType: PresetsByType[],
	selectedResources: SelectedResource[],
	isEditAstOn: boolean,
	pluginName?: string
): string[] => {
	let sharablePresets = sharablePresetByType
		.map(preset => preset.name)
		.filter((name): name is string => !!name);
	if (!isStoragePlugin(pluginName)) {
		return sharablePresets;
	}
	if (!isEditAstOn || !isEditAvailableForResources(selectedResources)) {
		sharablePresets = sharablePresets.filter(
			presetName => presetName !== PermissionName.Edit
		);
	}
	sharablePresets = sharablePresets.filter(
		presetName =>
			presetName === PermissionName.View ||
			presetName === PermissionName.Download ||
			presetName === PermissionName.Edit
	);
	return sharablePresets;
};

export const getPresetByTypeFromEvaluateResponse = (
	sharablePreset?: SharablePreset[],
	presetsByResourceType?: PresetsByResourceType
): PresetsByType[] => {
	if (!sharablePreset || !presetsByResourceType) {
		return [];
	}

	const result: PresetsByType[] = [];

	Object.keys(presetsByResourceType).forEach(resourceType => {
		const presets = presetsByResourceType[resourceType];

		if (!presets) {
			return;
		}

		const filteredPresets = presets.filter(preset => {
			const matchingSharablePreset = sharablePreset.find(sp => sp.rid === preset.rid);
			return matchingSharablePreset?.actions?.canShare === true;
		});

		result.push(...filteredPresets);
	});

	return result;
};

export const getUserList = (grantedPermissions?: ExistingPermission[]): User[] => {
	if (!grantedPermissions || grantedPermissions.length === 0) {
		return [] as User[];
	}

	// Get the principals with valid userId
	return grantedPermissions.filter(
		permission => permission.userId && permission.userId?.trim() !== ''
	);
};

export const getConditionsFromString = (
	conditionsString?: string,
	requestedConditions?: ConditionalLinkConditions[]
): ConditionalLinkConditions[] => {
	if (conditionsString === undefined || conditionsString === null) {
		return defaultConditions;
	}
	const allowedConditions = conditionsString.split(',').map(condition => {
		switch (condition.trim()) {
			case 'Anonymous':
				return ConditionalLinkConditions.Anonymous;
			case 'AnonymousWithUserInfo':
				return ConditionalLinkConditions.AnonymousWithUserInfo;
			case 'RequireLoginEmployeesAndClients':
				return ConditionalLinkConditions.RequireLoginEmployeesAndClients;
			default:
				return ConditionalLinkConditions.RequireLoginEmployeesOnly;
		}
	});
	if (requestedConditions) {
		return allowedConditions.filter(condition => requestedConditions.includes(condition));
	}
	return allowedConditions;
};

export const isStoragePlugin = (consumerPluginName: string | undefined) => {
	return !consumerPluginName || consumerPluginName === 'storage';
};

export const getConditionalLinkOptionsForFirstTimeByType = (
	settings: LinkSettings,
	selectedResources: SelectedResource[],
	sharablePreset: PresetsByType[],
	isEditAstOn: boolean,
	pluginName?: string
): ConditionalLinkOptions => {
	const availablePermissions = getPermissionsByTypeFromEvaluateResponse(
		sharablePreset,
		selectedResources,
		isEditAstOn,
		pluginName
	);
	const defaultCondition = settings.conditionalLink?.shareLinkSettings
		?.defaultCondition as ConditionalLinkConditions;
	let lastPermission = availablePermissions[availablePermissions.length - 1];
	if (isStoragePlugin(pluginName)) {
		if (lastPermission === PermissionName.Owner) {
			lastPermission = PermissionName.Edit;
		}

		if (lastPermission === PermissionName.Edit) {
			if (!isEditAvailableForResources(selectedResources)) {
				lastPermission = PermissionName.Download;
			}
		}

		if (
			(defaultCondition === ConditionalLinkConditions.Anonymous ||
				defaultCondition === ConditionalLinkConditions.AnonymousWithUserInfo) &&
			lastPermission === PermissionName.Edit
		) {
			lastPermission = PermissionName.Download;
		}
	}

	return {
		condition: defaultCondition,
		presetRID: getPresetInfoTypeByPermission(lastPermission, sharablePreset)?.rid,
		shareType: ConditionalLinkShareType.Share,
		expirationDays:
			settings.conditionalLink?.shareLinkSettings?.availableExpirationDays?.[0] ??
			DEFAULT_EXPIRATION_DAYS,
		notify: true,
		useLatestVersion: !isStoragePlugin(pluginName)
			? true
			: availablePermissions.includes(PermissionName.Edit)
			? true
			: settings.conditionalLink?.shareLinkSettings?.showLatestVersionCheckbox,
	};
};

export const getConditionalLinkOptionsForFirstTime = (
	settings: LinkSettings,
	selectedResources: SelectedResource[],
	sharablePreset: SharablePreset[],
	isEditAstOn: boolean,
	pluginName?: string
): ConditionalLinkOptions => {
	const availablePermissions = getPermissionsFromEvaluateResponse(
		sharablePreset,
		selectedResources,
		isEditAstOn,
		pluginName
	);
	const defaultCondition = settings.conditionalLink?.shareLinkSettings
		?.defaultCondition as ConditionalLinkConditions;
	let lastPermission = availablePermissions[availablePermissions.length - 1];
	if (isStoragePlugin(pluginName)) {
		if (lastPermission === PermissionName.Owner) {
			lastPermission = PermissionName.Edit;
		}

		if (lastPermission === PermissionName.Edit) {
			if (!isEditAvailableForResources(selectedResources)) {
				lastPermission = PermissionName.Download;
			}
		}

		if (
			(defaultCondition === ConditionalLinkConditions.Anonymous ||
				defaultCondition === ConditionalLinkConditions.AnonymousWithUserInfo) &&
			lastPermission === PermissionName.Edit
		) {
			lastPermission = PermissionName.Download;
		}
	}
	return {
		condition: defaultCondition,
		presetRID: getPresetInfoByPermission(lastPermission, sharablePreset)?.rid,
		shareType: ConditionalLinkShareType.Share,
		expirationDays:
			settings.conditionalLink?.shareLinkSettings?.availableExpirationDays?.[0] ??
			DEFAULT_EXPIRATION_DAYS,
		notify: true,
		useLatestVersion: !isStoragePlugin(pluginName)
			? true
			: availablePermissions.includes(PermissionName.Edit)
			? true
			: settings.conditionalLink?.shareLinkSettings?.showLatestVersionCheckbox,
	};
};

export const getPresetInfoByPermission = (
	permission: string | undefined,
	sharablePreset: SharablePreset[]
) => {
	if (!permission || permission === '') {
		return null;
	}
	return sharablePreset.find(preset => preset.name === permission);
};

export const getPresetInfoTypeByPermission = (
	permission: string | undefined,
	sharablePreset: PresetsByType[]
) => {
	if (!permission || permission === '') {
		return null;
	}
	return sharablePreset.find(preset => preset.name === permission);
};

export const getPresetInfoByPresetRID = (
	presetRID: string | null | undefined,
	sharablePreset: SharablePreset[]
) => {
	if (!presetRID) {
		return null;
	}
	return sharablePreset.find(preset => preset.rid === presetRID);
};

export const getPresetInfoByTypeByPresetRID = (
	presetRID: string | null | undefined,
	presetByType: PresetsByType[]
) => {
	if (!presetRID) {
		return null;
	}
	return presetByType.find(preset => preset.rid === presetRID);
};

export const getPermissionKeyFromContext = (
	presetRID: string | undefined | null,
	sharablePreset: SharablePreset[]
): string => {
	const preset = getPresetInfoByPresetRID(presetRID, sharablePreset);
	return preset?.name ?? '';
};

export const getPermissionKeyFromContextByType = (
	presetRID: string | undefined | null,
	sharablePreset: PresetsByType[]
): string => {
	const preset = getPresetInfoByTypeByPresetRID(presetRID, sharablePreset);
	return preset?.name ?? '';
};

export const getResourceListForVersionFlag = (
	selectedResource: string[],
	useLatestVersion: boolean
): string[] => {
	if (useLatestVersion) {
		return getRidsWithoutFileVersion(selectedResource);
	}
	return selectedResource;
};

export function getPreset(presets: PresetsByType[], resourceType: string): Preset[] {
	let filterPresets = presets.map(preset => ({
		rid: preset.rid,
		name: preset.name,
		permissions: preset.permissionsByType[resourceType],
		requiredRoles: preset.requiredRoles || null,
		presetDescription: preset.presetDescription || null,
		category: preset.category || null,
	}));
	if (resourceType === ResourceTypeLiteral.File) {
		filterPresets = filterPresets.filter(preset => preset.name !== PermissionName.Upload);
	}
	return filterPresets;
}

export function getAvailablePermissions(
	config: PermissionsConfigurationsByType
): AvailablePermissionSet[] {
	const allPermissions: AvailablePermissionSet[] = [];

	for (const resourceType in config.availablePermissionSetsByType) {
		if (config.availablePermissionSetsByType.hasOwnProperty(resourceType)) {
			allPermissions.push(...config.availablePermissionSetsByType[resourceType]);
		}
	}
	return allPermissions;
}

export const getResourceTypeKeysBasedOnRIDs = (
	availablePermissionSetsByType?: PermissionByType,
	rids?: string[]
): string[] => {
	if (!rids || rids.length === 0 || !availablePermissionSetsByType) {
		return [];
	}

	let resourceRids = rids;
	if (!isWatermarkRidsPresent(rids)) {
		resourceRids = getRidsWithoutFileVersion(rids);
	}
	const keys = Object.keys(availablePermissionSetsByType).filter(resourceType =>
		resourceRids.some(rid => RID.parse(rid).entityType === resourceType)
	);

	return keys;
};

export function getRidsForEntityType(
	entityType: string,
	rids: string[],
	includeFileVersions = false
): string[] {
	if (entityType === ResourceTypeLiteral.File && includeFileVersions) {
		return rids.filter(rid => {
			const parsedId = RID.parse(rid);
			return (
				parsedId.entityType === ResourceTypeLiteral.File ||
				parsedId.entityType === ResourceTypeLiteral.FileVersion
			);
		});
	}
	if (entityType === ResourceTypeLiteral.WatermarkedFileVersionResourceType) {
		return rids.filter(rid => {
			const parsedId = RID.parse(rid);
			return (
				parsedId.entityType === ResourceTypeLiteral.WatermarkedFileVersionResourceType
			);
		});
	}
	if (entityType === ResourceTypeLiteral.WatermarkedFileResourceType) {
		return rids.filter(rid => {
			const parsedId = RID.parse(rid);
			return parsedId.entityType === ResourceTypeLiteral.WatermarkedFileResourceType;
		});
	}

	return rids.filter(rid => {
		if (rid) {
			const withoutVersionRid = getRidWithoutFileVersion(rid);
			if (!withoutVersionRid) {
				return false;
			}
			const parsedId = RID.parse(withoutVersionRid);
			return parsedId.entityType === entityType;
		}
		return false;
	});
}

export const isConfigurationByResourceType = (
	permissionByType?: PermissionByType,
	presetByResourceType?: PresetsByResourceType
): boolean => {
	if (!permissionByType || !presetByResourceType) {
		return false;
	}

	return (
		Object.keys(permissionByType).length > 0 &&
		Object.keys(presetByResourceType).length > 0
	);
};

export const mapPermissionsByType = (
	permissionsByType: PermissionsOptionsByType,
	keys: string[]
): PermissionsSelectionByType => {
	return keys.reduce((acc, key) => {
		const permissions = permissionsByType[key];
		if (permissions) {
			acc[key] = permissions.map(p => PermissionId.parse(p));
		}
		return acc;
	}, {} as PermissionsSelectionByType);
};

export const convertToPermissionsOptionsByType = (
	permissionsByType?: PermissionsSelectionByType
): PermissionsOptionsByType => {
	const result: PermissionsOptionsByType = {};

	if (!permissionsByType) {
		return result;
	}

	Object.entries(permissionsByType).forEach(([resourceType, permissions]) => {
		result[resourceType] = permissions.map(permission => permission.toString());
	});

	return result;
};

export const isPermissionMatching = (
	relatedResource: SharableRelatedResource,
	permissionsOptionsByType: PermissionsOptionsByType
): boolean => {
	if (!permissionsOptionsByType || Object.keys(permissionsOptionsByType).length === 0) {
		return false;
	}

	return (
		relatedResource?.isRequiredWhenOneOfSelectedPermissions?.every(requiredPermission => {
			const [resourceType] = requiredPermission.split(':');
			const options = permissionsOptionsByType[resourceType];

			if (!options) {
				return false;
			}
			return options.some(option => {
				const normalizedOption = `${resourceType}:${option.split(':').pop()}`;
				return normalizedOption === requiredPermission;
			});
		}) ?? false
	);
};

/*
RAM is giving days count excluding today's day, so we need to add offset. 
For example, if permission is granted for 90 days (PermissionExpiryInDays = 90), the expiration date is set as today + 89 days ( DateTimeOffset.UtcNow.AddDays(request.PermissionExpiryInDays.Value) )
We want the permission to be valid for the full 90 days, including today. Therefore, we need to add one additional day to account for the current day
*/

export const getOffsetedExpiryDays = (
	permissionExpiryInDays: number | null | undefined
): number | null => {
	if (permissionExpiryInDays == null || permissionExpiryInDays === undefined) {
		return null;
	}

	return permissionExpiryInDays + 1;
};

export const getResourceTypeFromPemissionKey = (key: string): string => {
	return key.split('-')[0] ?? '';
};

export const isMultiLeafTypesPresent = (resourceRids: string[]): boolean => {
	const rids = resourceRids.map(rid => getRidWithoutFileVersion(rid));
	const uniqueResourceTypes = new Set<string>();
	rids.forEach(rid => {
		const parsedRid = RID.parse(rid ?? '');
		const entityType = parsedRid.entityType;
		uniqueResourceTypes.add(entityType);
	});

	return uniqueResourceTypes.size > 1;
};

export const getPresetsByTypeForSelectedResources = (
	sharablePresetsByType: PresetsByType[],
	selectedResources: SelectedResource[]
): PresetsByType[] => {
	const rids = selectedResources.map(data => getRidWithoutFileVersion(data.rid));
	const uniqueResourceTypes = new Set<string>();
	rids.forEach(rid => {
		const parsedRid = RID.parse(rid ?? '');
		const entityType = parsedRid.entityType;
		uniqueResourceTypes.add(entityType);
	});

	if (
		uniqueResourceTypes.size > 1 ||
		(uniqueResourceTypes.size === 1 && uniqueResourceTypes.has(ResourceTypeLiteral.File))
	) {
		return sharablePresetsByType.filter(preset => preset.name !== PermissionName.Upload);
	}
	return sharablePresetsByType;
};

export const arePermissionsOptionsByTypeEqual = (
	permissionsOptionsByType1: PermissionsOptionsByType,
	permissionsOptionsByType2: PermissionsOptionsByType
): boolean => {
	const keys1 = Object.keys(permissionsOptionsByType1);
	const keys2 = Object.keys(permissionsOptionsByType2);

	if (keys1.length !== keys2.length) {
		return false;
	}

	for (const key of keys1) {
		const options1 = permissionsOptionsByType1[key];
		const options2 = permissionsOptionsByType2[key];

		if (!options2 || options1.length !== options2.length) {
			return false;
		}

		for (const option of options1) {
			if (!options2.includes(option)) {
				return false;
			}
		}
	}

	return true;
};

export const getUnifiedResourceKey = (resourceKeys: string[]): string => {
	const sortedKeys = [...resourceKeys].sort();
	return sortedKeys.join('_');
};

export const isEditingAvailable = (
	resources: SelectedResource[],
	account: Account
): boolean => {
	const isEditableResources = isEditAvailableForResources(resources);

	if (
		!account.Preferences?.IsHIPAA ||
		isMultiLeafTypesPresent(resources.map(r => r.rid))
	) {
		return isEditableResources;
	}
	const isOfficeOnlineEditingEnabled =
		account.Preferences?.EnableOfficeOnlineEditing ?? false;
	const rid = RID.parse(getRidWithoutFileVersion(resources[0].rid) ?? '');
	if (rid.entityType === ResourceTypeLiteral.File && !isOfficeOnlineEditingEnabled) {
		return false;
	}

	return isEditableResources;
};

export const getFilteredPresetsByType = (
	sharablePresetsByType: PresetsByType[],
	availablePermissions: string[]
): PresetsByType[] => {
	return sharablePresetsByType.filter(preset =>
		availablePermissions.includes(preset.name!)
	);
};

export const getFilteredPresets = (
	sharablePresets: SharablePreset[],
	availablePermissions: string[]
): SharablePreset[] => {
	return sharablePresets.filter(preset => availablePermissions.includes(preset.name!));
};

export const isEditOperation = (
	linkOptionsContentType: ConditionalLinkOptionsModalContentType
): boolean => {
	return (
		linkOptionsContentType ===
			ConditionalLinkOptionsModalContentType.EditExistingLinkOption ||
		linkOptionsContentType ===
			ConditionalLinkOptionsModalContentType.EditLinkOptionBeforeFirstLinkCreation
	);
};
